/* =====================================================================
 *  TEACHER PORTAL + panels
 * ===================================================================== */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { schoolSystemApi, downloadPdf } from "@/lib/schoolSystemApi";
import {
  Chalkboard, ArrowLeft, Plus, Printer, Money, SmileyWink,
  PencilSimple, Trash, Check, X,
} from "@phosphor-icons/react";
import { F, SelectField } from "./shared";
import { SchoolBrandingHeader } from "./BrandingAndPosition";

export default function TeacherPortal() {
  const { schoolId } = useParams();
  const params = new URLSearchParams(window.location.search);
  const grade = params.get("grade") || "";
  const [tab, setTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [myAssignments, setMyAssignments] = useState(null);   // null=loading

  // No grade in URL → fetch this teacher's assignments so we can show
  // a class picker (or auto-navigate when only one match exists).
  useEffect(() => {
    if (grade || !schoolId) return;
    schoolSystemApi.myTeacherAssignments(schoolId)
      .then(r => {
        const items = r.items || [];
        setMyAssignments(items);
        if (items.length === 1) {
          const t = items[0];
          window.location.replace(
            `/school-system/${t.school_id}/teacher?grade=${encodeURIComponent(t.grade)}`);
        }
      })
      .catch(() => setMyAssignments([]));
  }, [schoolId, grade]);

  const loadAll = async () => {
    try {
      const [{ items: s }, { items: subs }] = await Promise.all([
        schoolSystemApi.listStudents(schoolId, grade),
        schoolSystemApi.listSubjects(schoolId, grade),
      ]);
      setStudents(s || []); setSubjects(subs || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { if (grade) loadAll(); /* eslint-disable-line */ }, [schoolId, grade]);

  // ------- Grade selector when no grade in URL -------
  if (!grade) {
    return (
      <div className="max-w-lg mx-auto p-6 md:p-10 space-y-6" data-testid="teacher-grade-selector">
        <Link to="/school-system" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
          <ArrowLeft size={14} weight="bold" /> School System
        </Link>
        <h1 className="font-heading text-2xl md:text-3xl font-black flex items-center gap-2">
          <Chalkboard size={26} weight="fill" className="text-amber-400" /> Pick a class
        </h1>
        <p className="text-white/60 text-sm">
          You are assigned to more than one grade — choose the one you want to
          work on. You can come back here at any time to switch.
        </p>
        {myAssignments === null && <p className="text-white/50 text-sm">Loading your assignments…</p>}
        {myAssignments && myAssignments.length === 0 && (
          <p className="text-amber-300 text-sm">
            You have no active assignments for this school. Ask the head-teacher to add you.
          </p>
        )}
        <div className="space-y-2">
          {(myAssignments || []).map((t, i) => (
            <button key={`${t.school_id}-${t.grade}-${i}`}
                    data-testid={`teacher-grade-pick-${i}`}
                    onClick={() => window.location.assign(
                      `/school-system/${t.school_id}/teacher?grade=${encodeURIComponent(t.grade)}`)}
                    className="w-full flex items-center justify-between p-3 rounded-md
                               bg-white/[0.03] hover:bg-white/[0.08] border border-white/10
                               text-left">
              <div>
                <p className="font-heading font-bold">{t.grade}</p>
                <p className="text-xs text-white/50">{t.school_name || t.school_id}</p>
              </div>
              <span className="text-white/40 text-lg">→</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="school-portal max-w-6xl mx-auto p-4 md:p-8 space-y-8" data-testid="teacher-portal">
      <Link to="/school-system" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
        <ArrowLeft size={14} weight="bold" /> School System
      </Link>
      <SchoolBrandingHeader schoolId={schoolId} portal="teacher" />
      <h1 className="font-heading text-2xl font-black flex items-center gap-2">
        <Chalkboard size={26} weight="fill" className="text-amber-400" /> Teacher — <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">{grade}</Badge>
      </h1>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[#121212] border border-white/10 flex-wrap gap-1 p-1 h-auto w-full justify-start">
          <TabsTrigger value="students" data-testid="teacher-tab-students">Students</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="teacher-tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="records" data-testid="teacher-tab-records">Records</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="teacher-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="homework" data-testid="teacher-tab-homework">Homework</TabsTrigger>
          <TabsTrigger value="behaviour" data-testid="teacher-tab-behaviour">Behaviour</TabsTrigger>
          <TabsTrigger value="fees" data-testid="teacher-tab-fees">Fees</TabsTrigger>
          <TabsTrigger value="lesson" data-testid="teacher-tab-lesson">Lesson Notes</TabsTrigger>
          <TabsTrigger value="ai-tests" data-testid="teacher-tab-ai-tests">Tests</TabsTrigger>
          <TabsTrigger value="marking" data-testid="teacher-tab-marking">Marking Schemes</TabsTrigger>
          <TabsTrigger value="performance" data-testid="teacher-tab-perf">Performance</TabsTrigger>
          <TabsTrigger value="entries" data-testid="teacher-tab-entries">My entries</TabsTrigger>
          <TabsTrigger value="approved" data-testid="teacher-tab-approved">Approved</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-8 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-white/10 action-bar">
            <p className="text-sm text-white/70">{students.length} student(s) in {grade}</p>
            <Button data-testid="add-student-btn" onClick={() => setOpenAdd(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold gap-1">
              <Plus size={14} weight="bold" /> Add student
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {students.map((s) => (
              <Card key={s.student_id} className="bg-[#121212] border-white/10" data-testid={`student-row-${s.student_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-heading font-bold truncate">{s.student_name}</p>
                    <p className="text-xs text-white/50">{s.student_id} · {s.gender || ""}</p>
                    <p className="text-[11px] text-white/40">Parent: {s.parent_name || "—"} · {s.parent_contact || "—"}</p>
                    {(s.date_of_birth || s.admission_date) && (
                      <p className="text-[11px] text-white/30">
                        {s.date_of_birth ? `DOB ${s.date_of_birth}` : ""}
                        {s.admission_date ? ` · Admitted ${s.admission_date}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline"
                            data-testid={`student-edit-${s.student_id}`}
                            onClick={() => setEditStudent(s)}
                            className="border-white/20 bg-transparent hover:bg-white/5">
                      Edit
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`student-manage-${s.student_id}`}
                            onClick={() => setSelectedStudent(s)}
                            className="border-white/20 bg-transparent hover:bg-white/5">
                      Manage
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`student-delete-${s.student_id}`}
                            onClick={async () => {
                              if (!window.confirm(`Withdraw ${s.student_name}? History is preserved but they'll no longer appear in class lists.`)) return;
                              try {
                                await schoolSystemApi.deleteStudent(s.student_id);
                                toast.success(`${s.student_name} withdrawn`);
                                loadAll();
                              } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                            }}
                            className="border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="subjects" className="mt-8">
          <SubjectsPanel schoolId={schoolId} grade={grade} subjects={subjects} onChange={loadAll} />
        </TabsContent>
        <TabsContent value="records" className="mt-8">
          <RecordsPanel schoolId={schoolId} students={students} subjects={subjects} />
        </TabsContent>
        <TabsContent value="attendance" className="mt-8">
          <AttendancePanel schoolId={schoolId} grade={grade} students={students} />
        </TabsContent>
        <TabsContent value="homework" className="mt-8">
          <HomeworkPanel schoolId={schoolId} grade={grade} subjects={subjects} students={students} />
        </TabsContent>
        <TabsContent value="behaviour" className="mt-8">
          <BehaviourPanel schoolId={schoolId} students={students} />
        </TabsContent>
        <TabsContent value="fees" className="mt-8">
          <FeesPanel schoolId={schoolId} grade={grade} students={students} />
        </TabsContent>
        <TabsContent value="lesson" className="mt-8">
          <LessonNotesPanel schoolId={schoolId} grade={grade} subjects={subjects} />
        </TabsContent>
        <TabsContent value="ai-tests" className="mt-8">
          <AiTestsPanel schoolId={schoolId} grade={grade} subjects={subjects}/>
        </TabsContent>
        <TabsContent value="marking" className="mt-8">
          <MarkingSchemesPanel schoolId={schoolId} grade={grade}/>
        </TabsContent>
        <TabsContent value="performance" className="mt-8">
          <PerformancePanel schoolId={schoolId} grade={grade} subjects={subjects}/>
        </TabsContent>
        <TabsContent value="entries" className="mt-8">
          <MyEntriesPanel schoolId={schoolId}/>
        </TabsContent>
        <TabsContent value="approved" className="mt-8">
          <TeacherApprovedHistoryPanel schoolId={schoolId}/>
        </TabsContent>
      </Tabs>

      {openAdd && (
        <AddStudentDialog
          schoolId={schoolId} grade={grade}
          onClose={() => setOpenAdd(false)}
          onCreated={() => { setOpenAdd(false); loadAll(); }}
        />
      )}
      {selectedStudent && (
        <StudentManageDialog
          student={selectedStudent}
          subjects={subjects}
          onClose={() => setSelectedStudent(null)}
          onChanged={loadAll}
        />
      )}
      {editStudent && (
        <EditStudentDialog
          student={editStudent}
          onClose={() => setEditStudent(null)}
          onSaved={() => { setEditStudent(null); loadAll(); }}
        />
      )}
    </div>
  );
}

/* -------- Add-student dialog with auto-generated ID card -------- */
function AddStudentDialog({ schoolId, grade, onClose, onCreated }) {
  const [form, setForm] = useState({
    student_name: "", date_of_birth: "", gender: "", parent_name: "",
    parent_contact: "", parent_email: "", residential_address: "",
    admission_date: new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.student_name.trim()) return toast.error("Student name is required");
    setSaving(true);
    try {
      const s = await schoolSystemApi.createStudent({ school_id: schoolId, grade, ...form });
      setIssued(s);
      toast.success("Student ID generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const printCard = () => window.print();

  return (
    <Dialog open={true} onOpenChange={(o) => !o && (issued ? onCreated() : onClose())}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">
          {issued ? "Student ID issued" : `Add student — ${grade}`}
        </DialogTitle></DialogHeader>
        {!issued ? (
          <div className="space-y-3">
            <F label="Full name*"><Input data-testid="new-student-name" value={form.student_name} onChange={(e)=>set("student_name",e.target.value)} className="bg-white/5 border-white/10"/></F>
            <div className="grid grid-cols-2 gap-2">
              <F label="Date of birth"><Input type="date" value={form.date_of_birth||""} onChange={(e)=>set("date_of_birth",e.target.value)} className="bg-white/5 border-white/10"/></F>
              <F label="Gender">
                <Select value={form.gender} onValueChange={(v)=>set("gender",v)}>
                  <SelectTrigger className="bg-white/5 border-white/10"><SelectValue placeholder="Select"/></SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] text-white">
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
            <F label="Parent / Guardian name"><Input value={form.parent_name} onChange={(e)=>set("parent_name",e.target.value)} className="bg-white/5 border-white/10"/></F>
            <div className="grid grid-cols-2 gap-2">
              <F label="Parent contact (with + code)"><Input value={form.parent_contact} onChange={(e)=>set("parent_contact",e.target.value)} placeholder="+233..." className="bg-white/5 border-white/10"/></F>
              <F label="Parent email"><Input type="email" value={form.parent_email} onChange={(e)=>set("parent_email",e.target.value)} className="bg-white/5 border-white/10"/></F>
            </div>
            <F label="Residential address"><Textarea rows={2} value={form.residential_address} onChange={(e)=>set("residential_address",e.target.value)} className="bg-white/5 border-white/10"/></F>
            <F label="Admission date"><Input type="date" value={form.admission_date} onChange={(e)=>set("admission_date",e.target.value)} className="bg-white/5 border-white/10"/></F>
          </div>
        ) : (
          <div className="print:bg-white print:text-black bg-white/5 p-6 rounded-lg space-y-3 text-center" id="student-id-card">
            <p className="text-xs uppercase tracking-widest text-white/60 print:text-black">Student ID Card — keep safely</p>
            <p className="text-4xl font-heading font-black tracking-wider" data-testid="issued-student-id">{issued.student_id}</p>
            <div className="text-sm space-y-1">
              <p><b>{issued.student_name}</b></p>
              <p className="text-white/70 print:text-gray-600">{issued.grade}</p>
              {issued.parent_name && <p className="text-white/50 print:text-gray-500">Parent: {issued.parent_name}</p>}
            </div>
            <p className="text-[11px] text-white/50 print:text-gray-500">
              Parents access <b>Tain School System</b> using only this ID. Please deliver it securely.
            </p>
          </div>
        )}
        <DialogFooter>
          {!issued ? (
            <>
              <Button variant="outline" className="border-white/20 bg-transparent hover:bg-white/5" onClick={onClose}>Cancel</Button>
              <Button data-testid="new-student-save" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                {saving ? "Generating…" : "Create + generate ID"}
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="print-student-id" variant="outline" onClick={printCard} className="border-white/20 bg-transparent hover:bg-white/5 gap-2">
                <Printer size={14} weight="bold"/> Print
              </Button>
              <Button onClick={onCreated} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------- Edit-student dialog (bio, parent, DOB, admission date, grade) -------- */
function EditStudentDialog({ student, onClose, onSaved }) {
  const [form, setForm] = useState({
    student_name: student.student_name || "",
    date_of_birth: student.date_of_birth || "",
    gender: student.gender || "",
    parent_name: student.parent_name || "",
    parent_contact: student.parent_contact || "",
    parent_email: student.parent_email || "",
    residential_address: student.residential_address || "",
    admission_date: student.admission_date || "",
    grade: student.grade || "",
    extra_info: student.extra_info || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.student_name.trim()) return toast.error("Student name is required");
    setSaving(true);
    try {
      await schoolSystemApi.updateStudent(student.student_id, form);
      toast.success("Student updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg"
                      data-testid="edit-student-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Edit student — {student.student_id}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <F label="Full name*">
            <Input data-testid="edit-student-name"
                   value={form.student_name}
                   onChange={(e) => set("student_name", e.target.value)}
                   className="bg-white/5 border-white/10" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Date of birth">
              <Input type="date" data-testid="edit-student-dob"
                     value={form.date_of_birth || ""}
                     onChange={(e) => set("date_of_birth", e.target.value)}
                     className="bg-white/5 border-white/10" />
            </F>
            <F label="Gender">
              <Select value={form.gender || "__none__"}
                       onValueChange={(v) => set("gender", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="edit-student-gender"
                                className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] text-white">
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Parent / Guardian name">
            <Input data-testid="edit-parent-name"
                   value={form.parent_name}
                   onChange={(e) => set("parent_name", e.target.value)}
                   className="bg-white/5 border-white/10" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Parent contact">
              <Input data-testid="edit-parent-contact"
                     value={form.parent_contact}
                     onChange={(e) => set("parent_contact", e.target.value)}
                     placeholder="+233..."
                     className="bg-white/5 border-white/10" />
            </F>
            <F label="Parent email">
              <Input type="email" data-testid="edit-parent-email"
                     value={form.parent_email}
                     onChange={(e) => set("parent_email", e.target.value)}
                     className="bg-white/5 border-white/10" />
            </F>
          </div>
          <F label="Residential address">
            <Textarea rows={2} data-testid="edit-student-address"
                       value={form.residential_address}
                       onChange={(e) => set("residential_address", e.target.value)}
                       className="bg-white/5 border-white/10" />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Admission date">
              <Input type="date" data-testid="edit-student-admission"
                     value={form.admission_date || ""}
                     onChange={(e) => set("admission_date", e.target.value)}
                     className="bg-white/5 border-white/10" />
            </F>
            <F label="Class">
              <Input data-testid="edit-student-grade"
                     value={form.grade}
                     onChange={(e) => set("grade", e.target.value)}
                     placeholder="e.g. Grade 5"
                     className="bg-white/5 border-white/10" />
            </F>
          </div>
          <F label="Extra info / notes">
            <Textarea rows={2} data-testid="edit-student-extra"
                       value={form.extra_info || ""}
                       onChange={(e) => set("extra_info", e.target.value)}
                       className="bg-white/5 border-white/10" />
          </F>
        </div>
        <div className="flex gap-2 justify-end pt-3">
          <Button variant="outline" onClick={onClose}
                   className="border-white/20 bg-transparent hover:bg-white/5">
            Cancel
          </Button>
          <Button data-testid="edit-student-save"
                   disabled={saving} onClick={save}
                   className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SubjectsPanel({ schoolId, grade, subjects, onChange }) {
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await schoolSystemApi.createSubject({ school_id: schoolId, grade, name }); setName(""); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const beginEdit = (s) => { setEditingId(s.subject_id); setEditName(s.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };
  const saveEdit = async () => {
    if (!editName.trim()) return toast.error("Name required");
    try {
      await schoolSystemApi.updateSubject(editingId, { name: editName.trim() });
      toast.success("Subject updated"); cancelEdit(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete subject "${s.name}"? This will hide it from all forms.`)) return;
    try { await schoolSystemApi.archiveSubject(s.subject_id); toast.success("Subject deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="new-subject-name" value={name} onChange={(e)=>setName(e.target.value)} placeholder="e.g. Mathematics" className="bg-white/5 border-white/10"/>
        <Button data-testid="new-subject-add" onClick={add} className="bg-amber-500 hover:bg-amber-600 text-black font-bold gap-1"><Plus size={14} weight="bold"/> Add</Button>
      </div>
      <div className="pt-4 border-t border-white/10 space-y-3">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Current subjects</p>
        {subjects.length === 0 && <p className="text-white/50 text-sm">No subjects yet.</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {subjects.map((s) => (
            <div key={s.subject_id}
                  className="flex items-center gap-2 p-2 rounded bg-white/[0.03] border border-white/10"
                  data-testid={`subject-row-${s.subject_id}`}>
              {editingId === s.subject_id ? (
                <>
                  <Input value={editName} onChange={(e)=>setEditName(e.target.value)}
                          data-testid={`subject-edit-input-${s.subject_id}`}
                          className="bg-white/5 border-white/10 h-8 text-sm"/>
                  <Button size="sm" onClick={saveEdit}
                          data-testid={`subject-save-${s.subject_id}`}
                          className="bg-emerald-500 hover:bg-emerald-600 text-black">
                    <Check size={14} weight="bold"/>
                  </Button>
                  <Button size="sm" variant="outline" onClick={cancelEdit}
                          className="border-white/20 bg-transparent hover:bg-white/5">
                    <X size={14} weight="bold"/>
                  </Button>
                </>
              ) : (
                <>
                  <p className="flex-1 text-sm text-white/90 truncate">{s.name}</p>
                  <Button size="sm" variant="outline" onClick={()=>beginEdit(s)}
                          data-testid={`subject-edit-${s.subject_id}`}
                          className="border-white/20 bg-transparent hover:bg-white/5 h-8 px-2">
                    <PencilSimple size={14} weight="bold"/>
                  </Button>
                  <Button size="sm" variant="outline" onClick={()=>del(s)}
                          data-testid={`subject-delete-${s.subject_id}`}
                          className="border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 h-8 px-2">
                    <Trash size={14} weight="bold"/>
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RecordsPanel({ schoolId, students, subjects }) {
  const [studentId, setStudentId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [ca, setCa] = useState(""); const [exam, setExam] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1"); const [remark, setRemark] = useState("");
  const submit = async () => {
    if (!studentId || !subjectId) return toast.error("Pick a student and subject");
    try {
      await schoolSystemApi.submitRecord({
        school_id: schoolId, student_id: studentId, subject_id: subjectId,
        academic_year: year, term, ca_score: Number(ca||0), exam_score: Number(exam||0), remark,
      });
      toast.success("Submitted for approval"); setCa(""); setExam(""); setRemark("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <SelectField label="Student" testId="rec-student" value={studentId} onChange={setStudentId} options={students.map(s=>({value:s.student_id,label:s.student_name}))}/>
        <SelectField label="Subject" testId="rec-subject" value={subjectId} onChange={setSubjectId} options={subjects.map(s=>({value:s.subject_id,label:s.name}))}/>
        <F label="Term"><Input value={term} onChange={(e)=>setTerm(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Academic year"><Input value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Continuous Assessment"><Input data-testid="rec-ca" type="number" value={ca} onChange={(e)=>setCa(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Examination"><Input data-testid="rec-exam" type="number" value={exam} onChange={(e)=>setExam(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Remark"><Input value={remark} onChange={(e)=>setRemark(e.target.value)} className="bg-white/5 border-white/10"/></F>
      </div>
      <div className="pt-2 border-t border-white/10 flex items-center gap-4 flex-wrap">
        <Button data-testid="rec-submit" onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Submit for approval</Button>
        <p className="text-[11px] text-white/40">Records appear on the parent's report card only after the headteacher approves them.</p>
      </div>
    </div>
  );
}

function AttendancePanel({ schoolId, grade, students }) {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [marks, setMarks] = useState({});
  const [pdfDate, setPdfDate] = useState(new Date().toISOString().slice(0, 10));
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (sid, st) => setMarks((m) => ({ ...m, [sid]: st }));
  const submit = async () => {
    const entries = Object.entries(marks);
    if (!entries.length) return toast.error("Nothing to submit");
    for (const [sid, st] of entries) {
      try { await schoolSystemApi.markAttendance({ school_id: schoolId, student_id: sid, day, status: st }); }
      catch (e) { toast.error(e.response?.data?.detail || "Failed"); return; }
    }
    toast.success(`Attendance submitted for ${entries.length} student(s)`);
    setMarks({});
  };
  const downloadRegister = async () => {
    if (!pdfDate) return toast.error("Pick a date first");
    setPdfBusy(true);
    try {
      await downloadPdf(
        schoolSystemApi.attendancePdfPath(schoolId, pdfDate, grade),
        `attendance-${grade || "class"}-${pdfDate}.pdf`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "PDF failed");
    } finally { setPdfBusy(false); }
  };
  return (
    <div className="space-y-6">
      <F label="Day"><Input type="date" value={day} onChange={(e)=>setDay(e.target.value)} className="bg-white/5 border-white/10 max-w-xs"/></F>
      <div className="pt-4 border-t border-white/10 space-y-3">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Mark attendance</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {students.map((s) => (
            <div key={s.student_id} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between p-3 rounded bg-white/[0.03]">
              <p className="text-sm">{s.student_name}</p>
              <div className="flex gap-2 flex-wrap">
                {["present","late","absent"].map((st) => (
                  <Button
                    key={st}
                    data-testid={`att-${s.student_id}-${st}`}
                    size="sm"
                    variant={marks[s.student_id]===st?"default":"outline"}
                    onClick={()=>set(s.student_id,st)}
                    className={marks[s.student_id]===st ? "bg-amber-500 text-black" : "border-white/20 bg-transparent hover:bg-white/5"}
                  >
                    {st}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-2">
        <Button data-testid="att-submit" onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Submit attendance</Button>
      </div>
      <div className="pt-6 border-t border-white/10 space-y-3">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
          Print attendance register (PDF)
        </p>
        <p className="text-xs text-white/50 max-w-lg">
          Downloads a Unicode-safe printable roll call for {grade || "your class"} on the selected date.
          Every active student appears with their status (Present / Late / Absent / Unmarked) and totals.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <F label="Date">
            <Input type="date" value={pdfDate}
                   data-testid="att-pdf-date"
                   onChange={(e) => setPdfDate(e.target.value)}
                   className="bg-white/5 border-white/10 max-w-xs"/>
          </F>
          <Button data-testid="att-pdf-download"
                  onClick={downloadRegister} disabled={pdfBusy}
                  className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold gap-1">
            <Printer size={16} weight="bold" />
            {pdfBusy ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function HomeworkPanel({ schoolId, grade, subjects, students }) {
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const [subjectId, setSubjectId] = useState(""); const [due, setDue] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({
    topic: "", strand: "", substrand: "", indicator: "",
    kind: "homework", duration_min: 30, due_date: "",
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [items, setItems] = useState([]);
  const loadHw = async () => {
    try {
      const r = await schoolSystemApi.listHomework(schoolId, grade);
      setItems(r.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (schoolId && grade) loadHw(); /* eslint-disable-next-line */ }, [schoolId, grade]);
  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    try {
      await schoolSystemApi.createHomework({
        school_id: schoolId, grade, subject_id: subjectId || null,
        kind: "homework", title, description: desc, due_date: due || null,
      });
      toast.success("Homework submitted for approval"); setTitle(""); setDesc(""); setDue("");
      loadHw();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const aiSubjectLabel = subjects.find(s => s.subject_id === subjectId)?.name || "";
  const runAi = async () => {
    if (!subjectId) return toast.error("Pick a subject first");
    if (!aiSubjectLabel) return toast.error("Selected subject has no name");
    if (!aiForm.topic.trim()) return toast.error("Topic is required");
    setAiBusy(true);
    try {
      const r = await schoolSystemApi.aiGenerateHomework({
        school_id: schoolId, grade, subject_id: subjectId,
        subject: aiSubjectLabel, topic: aiForm.topic,
        strand: aiForm.strand || null,
        substrand: aiForm.substrand || null,
        indicator: aiForm.indicator || null,
        kind: aiForm.kind, due_date: aiForm.due_date || null,
        duration_min: Number(aiForm.duration_min) || 30,
      });
      if (r?.unavailable) {
        toast.error(r.message || "Homework unavailable for this topic");
        return;
      }
      toast.success("Homework submitted for headteacher approval");
      setAiOpen(false);
      setAiForm({ topic: "", strand: "", substrand: "", indicator: "",
                  kind: "homework", duration_min: 30, due_date: "" });
      loadHw();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || e.message || "Homework generation failed");
    } finally { setAiBusy(false); }
  };
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
          Manual homework
        </p>
        <Button size="sm" variant="outline"
                data-testid="hw-ai-open"
                onClick={() => setAiOpen(true)}
                className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
          Auto-generate
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F label="Title"><Input value={title} onChange={(e)=>setTitle(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <SelectField label="Subject" value={subjectId} onChange={setSubjectId} options={subjects.map(s=>({value:s.subject_id,label:s.name}))}/>
        <F label="Description" full><Textarea rows={3} value={desc} onChange={(e)=>setDesc(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <F label="Due date"><Input type="date" value={due} onChange={(e)=>setDue(e.target.value)} className="bg-white/5 border-white/10"/></F>
        <div className="md:col-span-2 pt-4 border-t border-white/10">
          <Button onClick={submit} data-testid="hw-submit" className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Submit homework</Button>
        </div>
      </div>
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-[#101010] border-white/10 max-w-lg" data-testid="hw-ai-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Auto-generate homework</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-white/50">
            Writes a curriculum-aligned {aiForm.kind} for <b>{grade}</b>
            {aiSubjectLabel ? <> · <b>{aiSubjectLabel}</b></> : null}.
            After you generate, it goes straight into the head-teacher's
            approval queue, then students see it in their portal.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <F label="Type">
              <select value={aiForm.kind}
                      data-testid="hw-ai-kind"
                      onChange={(e) => setAiForm({ ...aiForm, kind: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-white/5 border border-white/10 text-sm">
                <option value="homework">Homework</option>
                <option value="assignment">Assignment</option>
                <option value="project">Project</option>
                <option value="practical">Practical</option>
              </select>
            </F>
            <F label="Topic"><Input data-testid="hw-ai-topic"
                                     value={aiForm.topic}
                                     onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })}
                                     placeholder="e.g. Fractions of a whole"
                                     className="bg-white/5 border-white/10"/></F>
            <F label="Strand (optional)"><Input value={aiForm.strand}
                                                   onChange={(e) => setAiForm({ ...aiForm, strand: e.target.value })}
                                                   className="bg-white/5 border-white/10"/></F>
            <F label="Sub-strand (optional)"><Input value={aiForm.substrand}
                                                       onChange={(e) => setAiForm({ ...aiForm, substrand: e.target.value })}
                                                       className="bg-white/5 border-white/10"/></F>
            <F label="Indicator code (optional)"><Input value={aiForm.indicator}
                                                          onChange={(e) => setAiForm({ ...aiForm, indicator: e.target.value })}
                                                          placeholder="e.g. B4.1.1.1.1"
                                                          className="bg-white/5 border-white/10"/></F>
            <F label="Due date (optional)"><Input type="date" value={aiForm.due_date}
                                                    onChange={(e) => setAiForm({ ...aiForm, due_date: e.target.value })}
                                                    className="bg-white/5 border-white/10"/></F>
            <F label="Duration (min)"><Input type="number" min={5} max={300}
                                                value={aiForm.duration_min}
                                                onChange={(e) => setAiForm({ ...aiForm, duration_min: e.target.value })}
                                                className="bg-white/5 border-white/10"/></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}
                    data-testid="hw-ai-cancel">Cancel</Button>
            <Button onClick={runAi} disabled={aiBusy}
                    data-testid="hw-ai-run"
                    className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
              {aiBusy ? "Generating…" : "Generate & submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <HomeworkList
        schoolId={schoolId}
        items={items}
        students={students}
        onReload={loadHw}
      />
    </div>
  );
}

// -- Existing homework list w/ PDF, marking scheme, scores actions -----------

function HomeworkList({ schoolId, items, students, onReload }) {
  const [schemeFor, setSchemeFor] = useState(null); // homework object
  const [scoresFor, setScoresFor] = useState(null); // homework object
  if (!items?.length) {
    return (
      <div className="pt-6 border-t border-white/10 text-sm text-white/50">
        No homework yet. Create one above to get started.
      </div>
    );
  }
  return (
    <div className="pt-6 border-t border-white/10 space-y-3">
      <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
        Your homework ({items.length})
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((h) => (
          <Card key={h.homework_id} className="bg-[#121212] border-white/10">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-amber-300 font-bold">
                    {h.hw_label || `HW-${String(h.hw_number||0).padStart(4,"0")}`}
                  </p>
                  <p className="font-heading font-bold text-sm truncate">{h.title}</p>
                  <p className="text-xs text-white/60">Due {h.due_date || "—"} · {h.kind}</p>
                </div>
                <Badge className={h.approved
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "bg-amber-500/20 text-amber-300 border-amber-500/40"}>
                  {h.approved ? "Approved" : "Pending"}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline"
                        data-testid={`hw-pdf-${h.homework_id}`}
                        onClick={() => downloadPdf(
                          schoolSystemApi.homeworkPdfPath(h.homework_id),
                          `${h.hw_label || h.homework_id}.pdf`)}
                        className="border-white/20 bg-transparent hover:bg-white/5">
                  Download PDF
                </Button>
                {h.approved && (
                  <>
                    <Button size="sm" variant="outline"
                            data-testid={`hw-scheme-${h.homework_id}`}
                            onClick={() => setSchemeFor(h)}
                            className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
                      Marking scheme
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`hw-scores-${h.homework_id}`}
                            onClick={() => setScoresFor(h)}
                            className="border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200">
                      Enter scores
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {schemeFor && (
        <HomeworkSchemeDialog
          hw={schemeFor}
          onClose={() => { setSchemeFor(null); onReload && onReload(); }}
        />
      )}
      {scoresFor && (
        <HomeworkScoresDialog
          hw={scoresFor}
          students={students || []}
          onClose={() => { setScoresFor(null); onReload && onReload(); }}
        />
      )}
    </div>
  );
}

function HomeworkSchemeDialog({ hw, onClose }) {
  const [scheme, setScheme] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await schoolSystemApi.homeworkGetScheme(hw.homework_id);
      setScheme(r.scheme || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hw?.homework_id]);
  const generate = async () => {
    setBusy(true);
    try {
      const r = await schoolSystemApi.homeworkGenerateScheme(hw.homework_id);
      setScheme(r.scheme);
      toast.success(`Marking scheme generated — ${r.cost_credits} credits used`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!scheme?.scheme_id) return;
    setBusy(true);
    try {
      await schoolSystemApi.homeworkSubmitScheme(scheme.scheme_id);
      toast.success("Marking scheme submitted for headteacher approval");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const status = scheme?.approved ? "approved"
    : (scheme?.status || (scheme ? "draft" : "none"));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0e0e0e] border-white/10 max-w-2xl" data-testid="hw-scheme-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Marking scheme · {hw.hw_label}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-white/60">{hw.title}</p>
        {!scheme ? (
          <div className="py-6 text-sm text-white/60">
            No marking scheme yet.
            <div className="mt-3">
              <Button data-testid="hw-scheme-generate"
                      onClick={generate} disabled={busy}
                      className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                {busy ? "Generating…" : "Auto-generate"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Badge className={status === "approved"
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                : status === "submitted"
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                  : "bg-white/10 text-white/70"}>
                Status: {status}
              </Badge>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                        onClick={() => downloadPdf(
                          schoolSystemApi.homeworkSchemePdfPath(hw.homework_id),
                          `${hw.hw_label}-marking-scheme.pdf`)}
                        className="border-white/20 bg-transparent hover:bg-white/5">
                  Download PDF
                </Button>
                {status === "draft" && (
                  <Button size="sm" onClick={submit} disabled={busy}
                          data-testid="hw-scheme-submit"
                          className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                    {busy ? "Submitting…" : "Submit for approval"}
                  </Button>
                )}
                {status !== "approved" && (
                  <Button size="sm" variant="outline" onClick={generate} disabled={busy}
                          className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
                    Regenerate
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-white/60">Total marks: {scheme.total_marks}</p>
            <ol className="space-y-2 mt-2">
              {(scheme.content?.items || []).map((it, i) => (
                <li key={i} className="p-2 rounded bg-white/[0.03]">
                  <p className="font-medium text-white/90">{i+1}. {it.q}</p>
                  <p className="text-xs text-emerald-300 mt-1">
                    <b>Expected:</b> {it.expected_answer}
                  </p>
                  {it.marking_criteria && (
                    <p className="text-xs text-white/60 mt-1">
                      <b>Criteria:</b> {it.marking_criteria}
                    </p>
                  )}
                  <p className="text-[10px] text-amber-300 mt-1">Marks: {it.marks}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HomeworkScoresDialog({ hw, students, onClose }) {
  const [maxScore, setMaxScore] = useState(20);
  const [scores, setScores] = useState({});
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await schoolSystemApi.homeworkListScores(hw.homework_id);
      setExisting(r.items || []);
      const seed = {};
      (r.items || []).forEach((x) => { seed[x.student_id] = { score: x.score, remarks: x.remarks || "" }; });
      setScores(seed);
      const anyMax = (r.items || []).find(x => x.max_score);
      if (anyMax?.max_score) setMaxScore(anyMax.max_score);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hw?.homework_id]);

  const submit = async () => {
    const rows = students
      .map((s) => {
        const v = scores[s.student_id];
        if (!v || v.score === "" || v.score === undefined || v.score === null) return null;
        return {
          student_id: s.student_id,
          score: Number(v.score),
          max_score: Number(maxScore),
          remarks: v.remarks || "",
        };
      })
      .filter(Boolean);
    if (!rows.length) return toast.error("Enter at least one score");
    setBusy(true);
    try {
      const r = await schoolSystemApi.homeworkSubmitScores(hw.homework_id, rows);
      toast.success(`Submitted ${r.count} score(s) for headteacher approval`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0e0e0e] border-white/10 max-w-3xl" data-testid="hw-scores-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Enter scores · {hw.hw_label}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-white/60">{hw.title}</p>
        <div className="flex items-end gap-3 mt-2">
          <F label="Max score">
            <Input type="number" min={1} max={1000}
                   value={maxScore}
                   data-testid="hw-scores-max"
                   onChange={(e) => setMaxScore(e.target.value)}
                   className="bg-white/5 border-white/10 w-28"/>
          </F>
          <Button size="sm" variant="outline"
                  onClick={() => downloadPdf(
                    schoolSystemApi.homeworkScoresPdfPath(hw.homework_id),
                    `${hw.hw_label}-scores.pdf`)}
                  className="border-white/20 bg-transparent hover:bg-white/5">
            Download PDF
          </Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto mt-3 space-y-2">
          {students?.length ? students.map((s) => {
            const existingRow = existing.find(e => e.student_id === s.student_id);
            const isApproved = existingRow?.approved;
            const status = existingRow?.approved
              ? "approved"
              : existingRow ? (existingRow.status || "pending") : "";
            return (
              <div key={s.student_id}
                    className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] items-center gap-2 p-2 rounded bg-white/[0.03]">
                <div>
                  <p className="text-sm font-medium">{s.student_name}</p>
                  <p className="text-xs text-white/50">{s.student_id}</p>
                </div>
                <Input type="number" min={0} max={maxScore}
                       data-testid={`hw-score-${s.student_id}`}
                       value={scores[s.student_id]?.score ?? ""}
                       disabled={isApproved}
                       onChange={(e) => setScores({
                         ...scores,
                         [s.student_id]: { ...(scores[s.student_id]||{}), score: e.target.value },
                       })}
                       className="bg-white/5 border-white/10 w-24"/>
                <Input placeholder="Remarks (optional)"
                       value={scores[s.student_id]?.remarks ?? ""}
                       disabled={isApproved}
                       onChange={(e) => setScores({
                         ...scores,
                         [s.student_id]: { ...(scores[s.student_id]||{}), remarks: e.target.value },
                       })}
                       className="bg-white/5 border-white/10"/>
                {status && (
                  <Badge className={status === "approved"
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                    : "bg-amber-500/20 text-amber-300 border-amber-500/40"}>
                    {status}
                  </Badge>
                )}
              </div>
            );
          }) : <p className="text-sm text-white/50">No students in this class yet.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={busy}
                  data-testid="hw-scores-submit"
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
            {busy ? "Submitting…" : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LessonNotesPanel({ schoolId, grade, subjects }) {
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [week, setWeek] = useState(""); const [subjectId, setSubjectId] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({
    topic: "", strand: "", substrand: "", indicator: "",
    duration_min: 30, term: "Term 1", date: "",
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCost, setAiCost] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const loadMyNotes = async () => {
    setLoadingNotes(true);
    try {
      const r = await schoolSystemApi.listMyLessonNotes(schoolId, { grade });
      setMyNotes(r.items || []);
    } catch (e) {
      // Silent — teacher may just not have any notes yet
    } finally { setLoadingNotes(false); }
  };
  useEffect(() => { if (schoolId) loadMyNotes(); /* eslint-disable-next-line */ },
            [schoolId, grade]);

  const openPdf = (note_id) => {
    // Auto-download so mobile pop-up blockers don't kill the flow.
    const filename = `lesson-note-${note_id}.pdf`;
    downloadPdf(`/school-system/lesson-notes/${note_id}/pdf`, filename)
      .catch((e) => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: over 10 MB — skipped`);
          continue;
        }
        const att = await schoolSystemApi.uploadLessonNoteAttachment(schoolId, grade, f);
        setAttachments((prev) => [...prev, att]);
        toast.success(`Uploaded ${att.name}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeAttachment = (url) => setAttachments((prev) => prev.filter((a) => a.url !== url));

  const submit = async () => {
    if (!title || !body || !week) return toast.error("Week, title and body are required");
    try {
      await schoolSystemApi.submitLessonNote({
        school_id: schoolId, grade, subject_id: subjectId || null,
        week, title, body, attachments,
      });
      toast.success("Lesson note submitted for approval");
      setTitle(""); setBody(""); setWeek(""); setAttachments([]);
      loadMyNotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generateWithAi = async () => {
    const subject = subjects.find(s => s.subject_id === subjectId);
    if (!subject) return toast.error("Pick a subject first");
    if (!aiForm.topic) return toast.error("Topic is required");
    setAiBusy(true);
    try {
      const r = await schoolSystemApi.aiGenerateLessonNote({
        school_id: schoolId, grade, subject: subject.name,
        topic: aiForm.topic, strand: aiForm.strand, substrand: aiForm.substrand,
        indicator: aiForm.indicator, week, term: aiForm.term,
        duration_min: Number(aiForm.duration_min || 30),
        date: aiForm.date || undefined,
      });
      setAiCost(r.cost_credits);
      if (r.unavailable) {
        toast.info(r.message || "Lesson notes unavailable");
        return;
      }
      const n = r.note || {};
      // Auto-fill the form with the AI output so the teacher can edit & submit.
      setTitle(n.title || aiForm.topic);
      const parts = [];
      const push = (k, v) => v && parts.push(`### ${k}\n${Array.isArray(v) ? v.join("\n") : v}`);
      push("Class/Grade", n.grade || grade);
      push("Subject", n.subject || subject.name);
      push("Strand", n.strand); push("Sub-strand", n.substrand);
      push("Content Standard", n.content_standard);
      push("Indicator", n.indicator);
      push("Week", n.week); push("Term", n.term);
      push("Learning Objectives", n.learning_objectives);
      push("Previous Knowledge", n.previous_knowledge);
      push("Vocabulary", n.vocabulary);
      push("Resources", n.resources);
      push("Introduction", n.introduction);
      push("Teacher Activities", n.teacher_activities);
      push("Learner Activities", n.learner_activities);
      push("Main Activities", n.main_activities);
      push("Questions / Prompts", n.questions);
      push("Practical Activities", n.practical_activities);
      push("Differentiation", n.differentiation);
      push("Assessment", n.assessment);
      push("Conclusion", n.conclusion);
      push("Homework", n.homework);
      push("Reflection", n.reflection);
      setBody(parts.join("\n\n"));
      toast.success(`Generated — ${r.cost_credits} credits used. Edit if needed then Submit.`);
      setAiOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof d === "object") {
        toast.error(d.message || "Insufficient credits");
      } else {
        toast.error(typeof d === "string" ? d : "Generation failed");
      }
    } finally { setAiBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
      <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-bold text-lg">Lesson notes</h3>
          <p className="text-white/50 text-xs">Compose manually or auto-generate (charged per admin config).</p>
        </div>
        <Button data-testid="ai-lesson-note-open" onClick={() => setAiOpen(true)}
                className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold">
          ✨ Auto-generate
        </Button>
      </div>

      {/* My submitted notes — with printable Unicode-safe PDFs */}
      <div className="md:col-span-2 rounded-md bg-white/[0.02] border border-white/10 p-3"
            data-testid="my-lesson-notes">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">
            My submitted notes
            <span className="text-white/40 text-xs font-normal ml-2">
              ({myNotes.length})
            </span>
          </p>
          <Button size="sm" variant="ghost" onClick={loadMyNotes}
                   data-testid="my-lesson-notes-refresh"
                   className="text-white/70 hover:text-white h-7 px-2">
            {loadingNotes ? "…" : "Refresh"}
          </Button>
        </div>
        {myNotes.length === 0 && !loadingNotes && (
          <p className="text-white/40 text-xs">No notes yet — submit one below.</p>
        )}
        {myNotes.length > 0 && (
          <div className="max-h-56 overflow-y-auto divide-y divide-white/5">
            {myNotes.map((n) => {
              const st = (n.status || "draft").toLowerCase();
              const dec = n.approval?.decision;
              const badge = dec === "approved"
                ? "bg-emerald-500/20 text-emerald-300"
                : dec === "returned"
                ? "bg-rose-500/20 text-rose-300"
                : st === "submitted"
                ? "bg-amber-500/20 text-amber-300"
                : "bg-white/10 text-white/60";
              return (
                <div key={n.note_id}
                     className="py-2 flex items-center justify-between gap-3 text-sm"
                     data-testid={`my-lesson-note-${n.note_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate">
                      <span className="font-medium">{n.title || "Untitled"}</span>
                      <span className="text-white/40 text-xs ml-2">
                        {n.grade || ""} · Week {n.week || "—"}
                      </span>
                    </p>
                    <p className="text-white/40 text-xs">
                      {(n.created_at || "").slice(0, 10)}
                      {dec ? ` · ${dec}` : ""}
                      {n.approval?.comment ? ` · "${n.approval.comment}"` : ""}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${badge}`}>
                    {dec || st}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          onClick={() => openPdf(n.note_id)}
                          data-testid={`my-lesson-note-pdf-${n.note_id}`}>
                    Print PDF
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <F label="Week"><Input value={week} onChange={(e)=>setWeek(e.target.value)} placeholder="Week 3" className="bg-white/5 border-white/10"/></F>
      <SelectField label="Subject" value={subjectId} onChange={setSubjectId} options={subjects.map(s=>({value:s.subject_id,label:s.name}))}/>
      <F label="Title" full><Input value={title} onChange={(e)=>setTitle(e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Notes" full><Textarea rows={12} value={body} onChange={(e)=>setBody(e.target.value)} className="bg-white/5 border-white/10 font-mono text-xs"/></F>
      <div className="md:col-span-2 space-y-3 pt-4 border-t border-white/10">
        <F label="Attachments (PDF, DOCX, PPTX, images — max 10 MB each)">
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-dashed border-white/20 hover:border-white/40 cursor-pointer text-sm">
            <input
              type="file" multiple
              accept=".pdf,.doc,.docx,.ppt,.pptx,image/*"
              onChange={onFilesPicked} disabled={uploading}
              className="hidden" data-testid="lesson-attach-input"
            />
            {uploading ? "Uploading…" : "Attach files"}
          </label>
        </F>
        {attachments.length > 0 && (
          <div className="space-y-1" data-testid="lesson-attach-list">
            {attachments.map((a) => (
              <div key={a.url} className="flex items-center justify-between bg-white/[0.03] rounded px-2 py-1 text-sm">
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:text-sky-200 truncate">
                  📎 {a.name} <span className="text-white/40 text-xs">({a.size_kb} KB)</span>
                </a>
                <button onClick={() => removeAttachment(a.url)} className="text-red-400 hover:text-red-300 text-xs px-2" aria-label="Remove attachment">Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="md:col-span-2 pt-2 flex items-center gap-3">
        <Button onClick={submit} disabled={uploading} data-testid="lesson-submit"
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
          Submit lesson note
        </Button>
        {aiCost !== null && <span className="text-white/50 text-xs">Last run: {aiCost} credits</span>}
      </div>

      {aiOpen && (
        <Dialog open onOpenChange={(o)=>!o && setAiOpen(false)}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Auto-generate lesson note</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-white/60">
                Grounded on the current GES / NaCCA curriculum. Charged per admin config on every run.
              </p>
              <F label="Topic *"><Input data-testid="ai-note-topic" value={aiForm.topic} onChange={(e)=>setAiForm({...aiForm, topic: e.target.value})} className="bg-white/5 border-white/10"/></F>
              <div className="grid grid-cols-2 gap-2">
                <F label="Strand"><Input value={aiForm.strand} onChange={(e)=>setAiForm({...aiForm, strand: e.target.value})} className="bg-white/5 border-white/10"/></F>
                <F label="Sub-strand"><Input value={aiForm.substrand} onChange={(e)=>setAiForm({...aiForm, substrand: e.target.value})} className="bg-white/5 border-white/10"/></F>
                <F label="Indicator"><Input value={aiForm.indicator} onChange={(e)=>setAiForm({...aiForm, indicator: e.target.value})} className="bg-white/5 border-white/10"/></F>
                <F label="Duration (min)"><Input type="number" value={aiForm.duration_min} onChange={(e)=>setAiForm({...aiForm, duration_min: e.target.value})} className="bg-white/5 border-white/10"/></F>
                <SelectField label="Term" value={aiForm.term} onChange={(v)=>setAiForm({...aiForm, term: v})} options={["Term 1","Term 2","Term 3"].map(t=>({value:t,label:t}))}/>
                <F label="Date"><Input type="date" value={aiForm.date} onChange={(e)=>setAiForm({...aiForm, date: e.target.value})} className="bg-white/5 border-white/10"/></F>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setAiOpen(false)}>Cancel</Button>
              <Button data-testid="ai-note-run" disabled={aiBusy} onClick={generateWithAi}
                       className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold">
                {aiBusy ? "Generating…" : "Generate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StudentManageDialog({ student, onClose }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1");
  const pdfUrl = schoolSystemApi.reportCardPdfUrl(student.school_id, student.student_id, year, term);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{student.student_name}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <p><b>Student ID:</b> {student.student_id}</p>
          <p><b>Grade:</b> {student.grade}</p>
          <p><b>Parent:</b> {student.parent_name} · {student.parent_contact}</p>
          <p><b>Email:</b> {student.parent_email}</p>
        </div>
        <div className="pt-3 border-t border-white/10 space-y-2">
          <p className="text-xs uppercase tracking-widest text-white/50 font-bold">Download report card</p>
          <div className="grid grid-cols-2 gap-2">
            <F label="Academic year"><Input value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
            <F label="Term"><Input value={term} onChange={(e)=>setTerm(e.target.value)} className="bg-white/5 border-white/10"/></F>
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="teacher-download-report-pdf"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white font-bold text-sm"
          >
            <Printer size={14} weight="bold"/> Open / print PDF
          </a>
          <p className="text-[11px] text-white/40">
            Opens the same PDF sent to parents. Print or "Save as PDF" from your browser.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/20 bg-transparent hover:bg-white/5" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BehaviourPanel({ schoolId, students }) {
  const [studentId, setStudentId] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1");
  const [form, setForm] = useState({ conduct: "", attitude: "", interest: "", discipline: "" });
  const set = (k, v) => setForm((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!studentId) return toast.error("Pick a student");
    try {
      await schoolSystemApi.submitBehaviour({
        school_id: schoolId, student_id: studentId, academic_year: year, term, ...form,
      });
      toast.success("Behaviour submitted for approval");
      setForm({ conduct: "", attitude: "", interest: "", discipline: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
      <SelectField label="Student" testId="beh-student" value={studentId} onChange={setStudentId} options={students.map((s)=>({value:s.student_id,label:s.student_name}))} />
      <F label="Academic year"><Input value={year} onChange={(e)=>setYear(e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Term"><Input value={term} onChange={(e)=>setTerm(e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Conduct"><Textarea data-testid="beh-conduct" rows={2} value={form.conduct} onChange={(e)=>set("conduct",e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Attitude"><Textarea rows={2} value={form.attitude} onChange={(e)=>set("attitude",e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Interest"><Textarea rows={2} value={form.interest} onChange={(e)=>set("interest",e.target.value)} className="bg-white/5 border-white/10"/></F>
      <F label="Discipline"><Textarea rows={2} value={form.discipline} onChange={(e)=>set("discipline",e.target.value)} className="bg-white/5 border-white/10"/></F>
      <div className="md:col-span-2 pt-4 border-t border-white/10">
        <Button data-testid="beh-submit" onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-black font-bold gap-1">
          <SmileyWink size={14} weight="fill"/> Submit behaviour
        </Button>
      </div>
    </div>
  );
}

function FeesPanel({ schoolId, grade, students }) {
  // Iter 113: teachers no longer enter fees — the head-teacher is the
  // sole author. This tab now surfaces the same read-only ledger the
  // parents see, with PDF download for receipts + statements so the
  // teacher can print/share printed copies at PTA meetings.
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState("Term 1");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const load = async () => {
    if (!grade) return;
    setLoading(true);
    try {
      const r = await schoolSystemApi.feesByClass(schoolId, grade, {
        academic_year: year, term,
      });
      setRows(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load fees");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId, grade, year, term]);
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-3 text-xs text-amber-100">
        Fees are entered by the head-teacher. This tab shows the current
        ledger for your class so you can share printouts with parents.
      </div>
      <div className="flex flex-wrap gap-3 items-end">
        <F label="Academic year"><Input value={year} onChange={(e)=>setYear(e.target.value)}
                                          data-testid="teacher-fees-year"
                                          className="bg-white/5 border-white/10 w-32"/></F>
        <F label="Term">
          <select value={term} onChange={(e)=>setTerm(e.target.value)}
                  data-testid="teacher-fees-term"
                  className="w-32 h-10 px-3 rounded-md bg-white/5 border border-white/10 text-sm">
            {["Term 1","Term 2","Term 3","Semester 1","Semester 2"].map(o=>(
              <option key={o} value={o}>{o}</option>))}
          </select>
        </F>
      </div>
      {loading ? (
        <p className="text-white/60 text-sm">Loading fees…</p>
      ) : rows.length === 0 ? (
        <p className="text-white/60 text-sm">No fees recorded for {grade} · {year} · {term} yet.</p>
      ) : (
        <div className="rounded-md border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Student</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Term fee</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Arrears</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Paid</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Outstanding</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-white/50">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.student.student_id} className="odd:bg-white/[0.02]"
                     data-testid={`teacher-fee-row-${r.student.student_id}`}>
                  <td className="px-3 py-2">
                    <p className="font-medium">{r.student.student_name}</p>
                    <p className="text-xs text-white/50">{r.student.student_id}</p>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.totals.term_fees.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.totals.arrears.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.totals.paid.toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-mono ${r.totals.outstanding > 0 ? "text-rose-300" : "text-white/70"}`}>
                    {r.totals.outstanding.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <Button size="sm" variant="outline"
                            onClick={() => setSelected(r)}
                            data-testid={`teacher-fee-open-${r.student.student_id}`}
                            className="border-white/20 bg-transparent hover:bg-white/5">
                      Open
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => downloadPdf(
                              schoolSystemApi.studentStatementPdfPath(
                                r.student.student_id,
                                { academic_year: year, term }),
                              `fee-statement-${r.student.student_id}.pdf`)}
                            data-testid={`teacher-fee-stmt-${r.student.student_id}`}
                            className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200">
                      Statement PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && (
        <TeacherFeeDrawer row={selected}
                             year={year} term={term}
                             onClose={() => setSelected(null)}/>
      )}
    </div>
  );
}

function TeacherFeeDrawer({ row, year, term, onClose }) {
  const [details, setDetails] = useState(null);
  useEffect(() => {
    schoolSystemApi.feesForStudent(row.student.student_id, {
      academic_year: year, term,
    })
      .then(setDetails)
      .catch(() => setDetails(null));
  }, [row, year, term]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0e0e0e] border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Fees · {row.student.student_name}
          </DialogTitle>
        </DialogHeader>
        {!details ? (
          <p className="text-white/60 text-sm">Loading…</p>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto">
            {(details.fees || []).length === 0 && (
              <p className="text-white/50 text-sm">No fee rows for this term.</p>
            )}
            {(details.fees || []).map((f) => (
              <Card key={f.fee_id} className="bg-white/[0.03] border-white/10">
                <CardContent className="p-3 space-y-1">
                  <p className="font-heading font-bold">{f.fee_type}</p>
                  <p className="text-xs text-white/60">
                    {f.academic_year} · {f.term} · Due {f.due_date || "—"}
                  </p>
                  <div className="grid grid-cols-4 gap-2 text-xs mt-2">
                    <div><span className="text-white/50">Term fee: </span><b>{Number(f.amount_due||0).toFixed(2)}</b></div>
                    <div><span className="text-white/50">Arrears: </span><b>{Number(f.arrears||0).toFixed(2)}</b></div>
                    <div><span className="text-white/50">Paid: </span><b className="text-emerald-300">{Number(f.amount_paid||0).toFixed(2)}</b></div>
                    <div><span className="text-white/50">Balance: </span><b className="text-rose-300">
                      {Math.max((Number(f.amount_due||0) + Number(f.arrears||0)) - Number(f.amount_paid||0), 0).toFixed(2)}
                    </b></div>
                  </div>
                  {(f.receipts || []).length > 0 && (
                    <div className="pt-2 border-t border-white/10 mt-2">
                      <p className="text-[11px] uppercase tracking-widest text-white/50">Receipts</p>
                      <div className="mt-1 space-y-1">
                        {(f.receipts || []).map((rec) => (
                          <div key={rec.receipt_id} className="flex items-center justify-between gap-2 text-xs">
                            <span>{rec.receipt_number || rec.receipt_id} · {(rec.issued_at || "").slice(0,10)} · GHS {Number(rec.amount || 0).toFixed(2)}</span>
                            <Button size="sm" variant="outline"
                                    onClick={() => downloadPdf(
                                      schoolSystemApi.feeReceiptPdfPath(f.fee_id, rec.receipt_id),
                                      `receipt-${rec.receipt_number || rec.receipt_id}.pdf`)}
                                    className="border-white/20 bg-transparent hover:bg-white/5 h-7 px-2 text-[11px]">
                              PDF
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ------------------------------------------------------------------ *
 *  Phase 3c: My entries — teacher can remove their unapproved rows.  *
 * ------------------------------------------------------------------ */
function MyEntriesPanel({ schoolId }) {
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.listTeacherEntries(schoolId, kind || undefined);
      setRows(r.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId, kind]);
  const remove = async (ap) => {
    if (!window.confirm(`Remove this ${ap.kind} entry? This cannot be undone.`)) return;
    try {
      await schoolSystemApi.deleteTeacherEntry(ap.approval_id);
      toast.success("Removed"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  return (
    <div className="space-y-3" data-testid="teacher-my-entries">
      <div className="flex flex-wrap items-end gap-3">
        <F label="Kind">
          <select value={kind} onChange={e => setKind(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"
                  data-testid="my-entries-kind">
            <option value="">All kinds</option>
            <option value="record">Record</option>
            <option value="homework">Homework</option>
            <option value="behaviour">Behaviour</option>
            <option value="attendance">Attendance</option>
            <option value="fee">Fee</option>
            <option value="lesson_note">Lesson note</option>
          </select>
        </F>
        <Button variant="outline" onClick={load} data-testid="my-entries-refresh">Refresh</Button>
      </div>
      {loading && <p className="text-white/60 text-sm">Loading…</p>}
      {!loading && rows.length === 0 && <p className="text-white/50 text-sm">No pending / returned entries.</p>}
      <div className="space-y-2">
        {rows.map(ap => (
          <Card key={ap.approval_id} className="bg-[#121212] border-white/10" data-testid={`entry-${ap.approval_id}`}>
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-semibold uppercase tracking-widest text-xs text-white/50">
                  {ap.kind} · {ap.status}
                </p>
                <p className="text-white/90">
                  {ap.target?.student_name || ap.target?.title || ap.target?.subject_name || ap.approval_id}
                </p>
                {ap.status === "returned" && ap.return_reason && (
                  <p className="text-xs text-amber-400 mt-1">Returned: {ap.return_reason}</p>
                )}
              </div>
              <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10"
                      onClick={() => remove(ap)} data-testid={`entry-del-${ap.approval_id}`}>
                Remove
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


/* ------------------------------------------------------------------ *
 *  AI Tests panel — generate test/exam + marking scheme + submit.    *
 *  Backend: /api/school-system/tests/ai-generate + /submit +         *
 *           /marking-scheme/ai-generate   (charged per admin config) *
 * ------------------------------------------------------------------ */
function AiTestsPanel({ schoolId, grade, subjects }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({
    subject: "", topic: "", strand: "", substrand: "", total_questions: 20,
  });
  const [busy, setBusy] = useState(false);
  const [msBusy, setMsBusy] = useState({});
  const [lastCost, setLastCost] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.listTests(schoolId, { grade });
      setTests(r.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId, grade]);

  const generate = async () => {
    if (!form.subject) return toast.error("Pick a subject");
    setBusy(true);
    try {
      const r = await schoolSystemApi.aiGenerateTest({
        school_id: schoolId, grade,
        subject: form.subject, topic: form.topic || null,
        strand: form.strand || null, substrand: form.substrand || null,
        total_questions: Number(form.total_questions || 20),
      });
      setLastCost(r.cost_credits);
      if (r.unavailable) toast.info(r.message || "Lesson notes unavailable");
      else toast.success(`Draft test created — ${r.cost_credits} credits used.`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Generation failed"));
    } finally { setBusy(false); }
  };

  const submit = async (t) => {
    try {
      await schoolSystemApi.submitTest(t.test_id);
      toast.success("Submitted for headteacher approval");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const generateMs = async (t) => {
    setMsBusy(m => ({ ...m, [t.test_id]: true }));
    try {
      const r = await schoolSystemApi.aiGenerateMarkingScheme(t.test_id);
      toast.success(`Marking scheme generated — ${r.cost_credits} credits used.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Generation failed"));
    } finally { setMsBusy(m => ({ ...m, [t.test_id]: false })); }
  };

  return (
    <div className="space-y-4" data-testid="teacher-ai-tests">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Generate test / exam</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField label="Subject" value={form.subject} onChange={(v)=>setForm({...form, subject: v})}
                        options={subjects.map(s => ({ value: s.name, label: s.name }))}/>
          <F label="Total questions"><Input type="number" value={form.total_questions}
                                              onChange={(e)=>setForm({...form, total_questions: e.target.value})}
                                              className="bg-white/5 border-white/10"/></F>
          <F label="Topic"><Input value={form.topic}
                                    onChange={(e)=>setForm({...form, topic: e.target.value})}
                                    className="bg-white/5 border-white/10"/></F>
          <F label="Strand"><Input value={form.strand}
                                     onChange={(e)=>setForm({...form, strand: e.target.value})}
                                     className="bg-white/5 border-white/10"/></F>
          <F label="Sub-strand"><Input value={form.substrand}
                                          onChange={(e)=>setForm({...form, substrand: e.target.value})}
                                          className="bg-white/5 border-white/10"/></F>
          <div className="flex items-end gap-3">
            <Button data-testid="ai-test-generate" onClick={generate} disabled={busy}
                    className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold">
              {busy ? "Generating…" : "✨ Generate test"}
            </Button>
            {lastCost !== null && <span className="text-white/50 text-xs">Last run: {lastCost} credits</span>}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">My tests ({tests.length})</CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && tests.length === 0 && (
            <p className="text-white/50 text-sm">No tests yet. Generate your first one above.</p>
          )}
          <div className="space-y-2">
            {tests.map(t => (
              <div key={t.test_id} className="p-3 rounded-md bg-white/[0.02] border border-white/5"
                   data-testid={`ai-test-row-${t.test_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <p className="font-semibold">
                      {t.content?.title || `${t.subject} · ${t.topic || "test"}`}
                    </p>
                    <p className="text-white/50 text-xs">
                      {t.subject} · {t.grade} · {(t.content?.sections || []).length} section(s) ·
                      total marks {t.total_marks || t.content?.total_marks || "—"} · status <b>{t.status}</b>
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="ghost"
                            data-testid={`ai-test-view-${t.test_id}`}
                            onClick={() => setExpanded(x => ({ ...x, [t.test_id]: !x[t.test_id] }))}>
                      {expanded[t.test_id] ? "Hide questions" : "View questions"}
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`ai-test-pdf-${t.test_id}`}
                            onClick={() => downloadPdf(
                              `/school-system/tests/${t.test_id}/pdf`,
                              `test-${(t.subject || "").replace(/\W+/g,"-")}.pdf`,
                            ).catch(e => toast.error(e?.response?.data?.detail || e.message || "PDF failed"))}>
                      Print PDF
                    </Button>
                    {(t.status === "draft" || t.status === "returned") && (
                      <Button size="sm" onClick={() => submit(t)} data-testid={`ai-test-submit-${t.test_id}`}
                              className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                        Submit for approval
                      </Button>
                    )}
                    <Button size="sm" variant="outline"
                            data-testid={`ai-marking-${t.test_id}`}
                            disabled={!!msBusy[t.test_id]}
                            onClick={() => generateMs(t)}>
                      {msBusy[t.test_id] ? "Generating…" : "✨ Marking scheme"}
                    </Button>
                  </div>
                </div>
                {expanded[t.test_id] && (
                  <TestQuestionsView content={t.content} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* Human-readable rendering for a generated test's content JSON.
 * Sections → questions numbered per-section, with MCQ options and
 * marks labels. Unicode symbols (² ³ ½ π √ ∫ ∑ ≠ ≤ ≥ × ÷ α β θ Ω …)
 * render natively via the system font, no distortion. */
function TestQuestionsView({ content }) {
  const sections = content?.sections || [];
  if (sections.length === 0) {
    return (
      <p className="mt-2 text-white/50 text-xs italic">
        No questions were captured — try regenerating this test.
      </p>
    );
  }
  return (
    <div className="mt-3 space-y-3 max-h-[420px] overflow-y-auto pr-2"
          data-testid="test-questions-view">
      {content.title && (
        <p className="text-center font-bold text-sm">{content.title}</p>
      )}
      {sections.map((sec, si) => (
        <div key={si} className="rounded-md bg-black/30 border border-white/5 p-3">
          <p className="font-semibold text-sm text-sky-300">
            {sec.name || `Section ${si + 1}`}
            {sec.type ? <span className="ml-2 text-[10px] uppercase tracking-wider text-white/40">
              {sec.type.replace(/_/g, " ")}
            </span> : null}
          </p>
          {sec.instructions && (
            <p className="text-white/60 text-xs italic mt-1 whitespace-pre-wrap">
              {sec.instructions}
            </p>
          )}
          <ol className="mt-2 space-y-2 text-sm list-decimal list-inside">
            {(sec.questions || []).map((q, qi) => (
              <li key={qi} className="pl-1">
                <span className="whitespace-pre-wrap">{q.q}</span>
                {typeof q.marks !== "undefined" && (
                  <span className="ml-2 text-white/40 text-xs">
                    [{q.marks} mark{Number(q.marks) === 1 ? "" : "s"}]
                  </span>
                )}
                {Array.isArray(q.options) && q.options.length > 0 && (
                  <ul className="mt-1 ml-6 space-y-0.5 text-white/80 text-xs">
                    {q.options.map((opt, oi) => (
                      <li key={oi}>{opt}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
      {typeof content.total_marks !== "undefined" && (
        <p className="text-right text-xs text-white/50">
          Total marks: <b className="text-white">{content.total_marks}</b>
        </p>
      )}
    </div>
  );
}


/* Human-readable rendering for a marking scheme's content JSON.
 * Groups answers by section, prints each Q + expected answer +
 * acceptable alternatives + marks in plain prose (not code). */
function MarkingSchemeView({ content }) {
  const sections = content?.sections || [];
  if (sections.length === 0) {
    return (
      <p className="mt-2 text-white/50 text-xs italic">
        Scheme is empty — try regenerating.
      </p>
    );
  }
  return (
    <div className="mt-2 space-y-3 max-h-[420px] overflow-y-auto pr-2"
          data-testid="marking-scheme-view">
      {sections.map((sec, si) => (
        <div key={si} className="rounded-md bg-black/30 border border-white/5 p-3">
          <p className="font-semibold text-sm text-emerald-300">
            {sec.name || `Section ${si + 1}`}
          </p>
          <ol className="mt-2 space-y-3 text-sm list-decimal list-inside">
            {(sec.answers || []).map((a, ai) => (
              <li key={ai} className="pl-1">
                <span className="text-white/90 whitespace-pre-wrap">
                  <b>Q:</b> {a.q}
                </span>
                {typeof a.marks !== "undefined" && (
                  <span className="ml-2 text-white/40 text-xs">
                    [{a.marks} mark{Number(a.marks) === 1 ? "" : "s"}]
                  </span>
                )}
                <div className="mt-1 ml-6 text-white/80 whitespace-pre-wrap">
                  <b className="text-emerald-300">Answer:</b> {a.answer}
                </div>
                {Array.isArray(a.acceptable) && a.acceptable.length > 0 && (
                  <div className="mt-1 ml-6 text-white/60 text-xs">
                    <b>Also accept:</b> {a.acceptable.join(" · ")}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
      {typeof content.total_marks !== "undefined" && (
        <p className="text-right text-xs text-white/50">
          Total marks: <b className="text-white">{content.total_marks}</b>
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Performance panel — teacher enters student scores, submits to HT. *
 *  Backend: POST /api/school-system/student-results                  *
 *           GET /api/school-system/student-results                   *
 * ------------------------------------------------------------------ */
function PerformancePanel({ schoolId, grade, subjects }) {
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    subject: "", test_title: "", total_marks: 100,
    term: "Term 1", academic_year: String(new Date().getFullYear()),
    date: "",
  });
  const [scores, setScores] = useState({}); // student_id -> score

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        schoolSystemApi.listStudentsForGrade(schoolId, grade),
        schoolSystemApi.listStudentResults(schoolId, { grade }),
      ]);
      setStudents(sRes.items || []);
      setResults(rRes.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId, grade]);

  const submitOne = async (student) => {
    const raw = scores[student.student_id];
    const score = Number(raw);
    if (raw === "" || raw === undefined) return toast.error("Enter a score first");
    if (Number.isNaN(score) || score < 0 || score > Number(form.total_marks)) {
      return toast.error(`Score must be 0..${form.total_marks}`);
    }
    if (!form.subject) return toast.error("Pick a subject");
    if (!form.test_title) return toast.error("Enter a test title");
    try {
      await schoolSystemApi.submitStudentResult({
        school_id: schoolId, grade,
        subject: form.subject, test_title: form.test_title,
        student_id: student.student_id,
        admission_number: student.student_id,
        score, total_marks: Number(form.total_marks),
        term: form.term, academic_year: form.academic_year,
        date: form.date || undefined,
      });
      toast.success(`Saved ${student.student_name} (${score}/${form.total_marks})`);
      setScores(sc => ({ ...sc, [student.student_id]: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const submitAll = async () => {
    let ok = 0, fail = 0;
    for (const s of students) {
      const raw = scores[s.student_id];
      if (raw === "" || raw === undefined) continue;
      const score = Number(raw);
      if (Number.isNaN(score)) { fail++; continue; }
      try {
        await schoolSystemApi.submitStudentResult({
          school_id: schoolId, grade,
          subject: form.subject, test_title: form.test_title,
          student_id: s.student_id, admission_number: s.student_id,
          score, total_marks: Number(form.total_marks),
          term: form.term, academic_year: form.academic_year,
          date: form.date || undefined,
        });
        ok++;
      } catch { fail++; }
    }
    toast[fail ? "warning" : "success"](`Saved ${ok} · Failed ${fail}`);
    setScores({});
    load();
  };

  return (
    <div className="space-y-4" data-testid="teacher-performance">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Score sheet header</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SelectField label="Subject" value={form.subject}
                        onChange={(v)=>setForm({...form, subject: v})}
                        options={subjects.map(s => ({ value: s.name, label: s.name }))}/>
          <F label="Test / Exam title">
            <Input data-testid="perf-test-title" value={form.test_title}
                   onChange={(e)=>setForm({...form, test_title: e.target.value})}
                   className="bg-white/5 border-white/10" placeholder="Class Test 2 / End-of-Term Exam"/>
          </F>
          <F label="Total marks">
            <Input type="number" value={form.total_marks}
                   onChange={(e)=>setForm({...form, total_marks: e.target.value})}
                   className="bg-white/5 border-white/10"/>
          </F>
          <SelectField label="Term" value={form.term} onChange={(v)=>setForm({...form, term: v})}
                        options={["Term 1","Term 2","Term 3"].map(t=>({value:t,label:t}))}/>
          <F label="Academic year">
            <Input value={form.academic_year}
                   onChange={(e)=>setForm({...form, academic_year: e.target.value})}
                   className="bg-white/5 border-white/10"/>
          </F>
          <F label="Date">
            <Input type="date" value={form.date}
                   onChange={(e)=>setForm({...form, date: e.target.value})}
                   className="bg-white/5 border-white/10"/>
          </F>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="font-heading">Class roster ({students.length})</CardTitle>
          <Button data-testid="perf-submit-all" onClick={submitAll}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
            Save all scores
          </Button>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && students.length === 0 && (
            <p className="text-white/50 text-sm">No active students in this grade.</p>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-white/50 uppercase text-xs">
                <tr>
                  <th className="py-2 pr-3">#</th>
                  <th className="py-2 pr-3">Admission #</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">Save</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.student_id} className="border-t border-white/5" data-testid={`perf-row-${s.student_id}`}>
                    <td className="py-2 pr-3 text-white/50">{i+1}</td>
                    <td className="py-2 pr-3 font-mono">{s.student_id}</td>
                    <td className="py-2 pr-3">{s.student_name}</td>
                    <td className="py-2 pr-3">
                      <Input type="number" min="0" max={form.total_marks}
                             value={scores[s.student_id] ?? ""}
                             onChange={(e)=>setScores(sc => ({...sc, [s.student_id]: e.target.value}))}
                             className="bg-white/5 border-white/10 w-24"
                             data-testid={`perf-score-${s.student_id}`}/>
                    </td>
                    <td className="py-2 pr-3">
                      <Button size="sm" variant="outline"
                              onClick={()=>submitOne(s)}
                              data-testid={`perf-save-${s.student_id}`}>Save</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">My recent submissions ({results.length})</CardTitle></CardHeader>
        <CardContent>
          {results.length === 0 && <p className="text-white/50 text-sm">No submissions yet.</p>}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-white/50 uppercase text-xs">
                <tr>
                  <th className="py-2 pr-3">Student</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Test</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">%</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.result_id} className="border-t border-white/5">
                    <td className="py-2 pr-3">{r.student_id}</td>
                    <td className="py-2 pr-3">{r.subject}</td>
                    <td className="py-2 pr-3">{r.test_title || "—"}</td>
                    <td className="py-2 pr-3">{r.score} / {r.total_marks}</td>
                    <td className="py-2 pr-3">{r.percentage}%</td>
                    <td className="py-2 pr-3"><b>{r.status}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}



/* ------------------------------------------------------------------ *
 *  MarkingSchemesPanel — teacher generates + views + submits their   *
 *  marking schemes for approval by the headteacher.                  *
 * ------------------------------------------------------------------ */
function MarkingSchemesPanel({ schoolId, grade }) {
  const [tests, setTests] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        schoolSystemApi.listTests(schoolId, { grade }),
        schoolSystemApi.listMarkingSchemes(schoolId, { grade }),
      ]);
      setTests(t.items || []);
      setSchemes(m.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId, grade]);

  const generate = async (test) => {
    setBusy(b => ({ ...b, [test.test_id]: true }));
    try {
      const r = await schoolSystemApi.aiGenerateMarkingScheme(test.test_id);
      if (r.unavailable) toast.info(r.message || "Marking scheme unavailable");
      else toast.success(`Marking scheme generated — ${r.cost_credits} credits used.`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : (d || "Generation failed"));
    } finally {
      setBusy(b => ({ ...b, [test.test_id]: false }));
    }
  };

  const submit = async (scheme) => {
    try {
      await schoolSystemApi.submitMarkingScheme(scheme.scheme_id);
      toast.success("Submitted for headteacher approval");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    }
  };

  // Group marking schemes by test_id so we can render alongside each test.
  const schemesByTest = schemes.reduce((acc, s) => {
    (acc[s.test_id] = acc[s.test_id] || []).push(s);
    return acc;
  }, {});
  const orphanSchemes = schemes.filter(s =>
    !tests.some(t => t.test_id === s.test_id)
  );

  return (
    <div className="space-y-4" data-testid="teacher-marking-schemes">
      <p className="text-white/60 text-sm">
        Generate a marking scheme from each of your tests, review the answer key,
        then submit it to the headteacher for approval. Approved schemes become visible to parents.
      </p>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Your tests + marking schemes ({tests.length})</CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && tests.length === 0 && (
            <p className="text-white/50 text-sm">No tests yet. Create one in the "Tests" tab first.</p>
          )}
          <div className="space-y-3">
            {tests.map(t => {
              const my = schemesByTest[t.test_id] || [];
              return (
                <div key={t.test_id} className="p-3 rounded-md bg-white/[0.02] border border-white/5"
                     data-testid={`mk-row-${t.test_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <p className="font-semibold">{t.content?.title || `${t.subject} · ${t.topic || "test"}`}</p>
                      <p className="text-white/50 text-xs">
                        {t.subject} · {t.grade} · total marks {t.total_marks || "—"} · test status <b>{t.status}</b>
                      </p>
                    </div>
                    <Button size="sm" variant="outline"
                            data-testid={`mk-generate-${t.test_id}`}
                            disabled={!!busy[t.test_id]}
                            onClick={() => generate(t)}>
                      {busy[t.test_id] ? "Generating…" : "✨ Generate marking scheme"}
                    </Button>
                  </div>
                  {my.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {my.map(s => (
                        <div key={s.scheme_id} className="p-2 rounded bg-white/[0.03] text-sm"
                             data-testid={`mk-scheme-${s.scheme_id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span>
                              <b>Scheme</b> · status <b>{s.status}</b> · total marks {s.total_marks} ·
                              created {(s.created_at || "").slice(0, 19).replace("T", " ")}
                            </span>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost"
                                      onClick={() => setExpanded(x => ({ ...x, [s.scheme_id]: !x[s.scheme_id] }))}
                                      data-testid={`mk-toggle-${s.scheme_id}`}>
                                {expanded[s.scheme_id] ? "Hide" : "View"}
                              </Button>
                              {(s.status === "draft" || s.status === "returned") && (
                                <Button size="sm"
                                        onClick={() => submit(s)}
                                        data-testid={`mk-submit-${s.scheme_id}`}
                                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
                                  Submit for approval
                                </Button>
                              )}
                            </div>
                          </div>
                          {expanded[s.scheme_id] && (
                            <MarkingSchemeView content={s.content} />
                          )}
                          {expanded[s.scheme_id] && (
                            <div className="mt-2 flex gap-2 flex-wrap">
                              <Button size="sm" variant="outline"
                                      data-testid={`mk-pdf-${s.scheme_id}`}
                                      onClick={() => downloadPdf(
                                        `/school-system/marking-schemes/${s.scheme_id}/pdf`,
                                        `marking-scheme-${s.scheme_id}.pdf`,
                                      ).catch(e => toast.error(e?.response?.data?.detail || e.message || "PDF failed"))}>
                                Print scheme PDF
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {orphanSchemes.length > 0 && (
        <Card className="bg-white/[0.03] border-white/10">
          <CardHeader><CardTitle className="font-heading">Schemes for tests you no longer own ({orphanSchemes.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {orphanSchemes.map(s => (
                <p key={s.scheme_id} className="text-white/60 text-xs">
                  {s.subject} · scheme {s.scheme_id.slice(0, 10)}… · status <b>{s.status}</b>
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* ---------------------------------------------------------------------------
 * TEACHER APPROVED HISTORY  — mirror of HT ApprovedHistoryPanel scoped
 * to the teacher's own submissions. Every row has a Print PDF button
 * when a PDF endpoint exists (uses the same shared downloadPdf helper
 * so mobile pop-up blockers don't interfere).
 * ------------------------------------------------------------------------- */
const TEACHER_APPROVED_KINDS = [
  { value: "",                label: "All" },
  { value: "lesson_note",     label: "Lesson notes" },
  { value: "test",            label: "Tests" },
  { value: "marking_scheme",  label: "Marking schemes" },
  { value: "student_results", label: "Student results" },
  { value: "record",          label: "Records" },
  { value: "attendance",      label: "Attendance" },
  { value: "behaviour",       label: "Behaviour" },
  { value: "homework",        label: "Homework" },
  { value: "fee",             label: "Fees" },
  { value: "parent_message",  label: "Parent messages" },
];

function TeacherApprovedHistoryPanel({ schoolId }) {
  const [kind, setKind] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (kind) params.kind = kind;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await schoolSystemApi.teacherApprovalsHistory(schoolId, params);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (schoolId) load(); /* eslint-disable-next-line */ },
             [schoolId, kind, dateFrom, dateTo]);

  const rowTitle = (it) => {
    const t = it.target || it;
    switch (it._kind) {
      case "lesson_note":     return t.title || "Lesson note";
      case "test":            return t.content?.title || `${t.subject || ""} · ${t.topic || "test"}`;
      case "marking_scheme":  return `Scheme · ${it.target?.subject || t.subject || t.test_id}`;
      case "student_results": return `${t.test_title || t.subject || "Result"} · ${it._student_name || t.student_id}`;
      case "record":          return `${t.record_type || "Record"} · ${it._student_name || t.student_id || ""}`;
      case "attendance":      return `Attendance · ${it._student_name || t.student_id || ""} · ${(t.date || "").slice(0,10)}`;
      case "behaviour":       return `Behaviour · ${it._student_name || t.student_id || ""}`;
      case "homework":        return `Homework · ${t.title || t.subject || ""}`;
      case "fee":             return `Fee · ${it._student_name || t.student_id || ""} · GHS ${(t.amount_due || 0)}`;
      case "parent_message":  return `Message · ${(t.subject || t.body || "").slice(0, 60)}`;
      default:                return it._kind;
    }
  };
  const rowSub = (it) => {
    const bits = [];
    if (it._grade || it.target?.grade) bits.push(it._grade || it.target?.grade);
    if (it._subject_name) bits.push(it._subject_name);
    if (it._when) bits.push((it._when || "").slice(0, 16).replace("T", " "));
    return bits.join(" · ");
  };
  const printPdf = (it) => {
    if (!it._pdf_url) return;
    downloadPdf(it._pdf_url, `${it._kind}-${it._target_id}.pdf`)
      .catch(e => toast.error(e?.response?.data?.detail || e.message || "PDF failed"));
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(it => {
        const bag = [
          rowTitle(it), rowSub(it),
          it._student_name, it._subject_name, it._grade,
          it.decision_note, it._target_id,
          it.target?.subject, it.target?.topic, it.target?.title,
          it.target?.body, it.target?.student_name,
        ].filter(Boolean).join(" ").toLowerCase();
        return bag.includes(q);
      })
    : items;

  return (
    <div className="space-y-4" data-testid="teacher-approved-history">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">My approved history</CardTitle>
          <p className="text-white/50 text-xs mt-1">
            Every item of yours that the head-teacher has approved, newest first.
            Tap "Print PDF" to download a Unicode-safe printable copy where one is available.
          </p>
        </CardHeader>
        <CardContent>
          <Input data-testid="teacher-approved-search"
                 value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search students, subjects, topics…"
                 className="bg-white/5 border-white/10 mb-3"/>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                From date
              </label>
              <Input type="date" value={dateFrom}
                     data-testid="teacher-approved-date-from"
                     onChange={(e) => setDateFrom(e.target.value)}
                     className="bg-white/5 border-white/10"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">
                To date
              </label>
              <Input type="date" value={dateTo}
                     data-testid="teacher-approved-date-to"
                     onChange={(e) => setDateTo(e.target.value)}
                     className="bg-white/5 border-white/10"/>
            </div>
            <div className="flex items-end">
              <Button size="sm" variant="outline"
                      data-testid="teacher-approved-clear-filters"
                      onClick={() => { setDateFrom(""); setDateTo(""); }}
                      className="w-full">
                Clear dates
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {TEACHER_APPROVED_KINDS.map(k => (
              <Button key={k.value || "all"} size="sm"
                       variant={kind === k.value ? "default" : "outline"}
                       onClick={() => setKind(k.value)}
                       data-testid={`teacher-approved-filter-${k.value || "all"}`}
                       className={kind === k.value ? "bg-emerald-500 text-black font-bold" : ""}>
                {k.label}
              </Button>
            ))}
          </div>
          {loading && <p className="text-white/60 text-sm">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-white/50 text-sm">
              {q ? `No approved items matching "${query}".` : "No approved items yet."}
            </p>
          )}
          <div className="space-y-2">
            {filtered.map((it, i) => (
              <div key={`${it._kind}-${it._target_id}-${i}`}
                   className="p-3 rounded-md bg-white/[0.02] border border-white/5
                              flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`teacher-approved-row-${it._target_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">
                    <span className="text-emerald-300 text-[10px] uppercase tracking-widest mr-2">
                      {(it._kind || "").replace(/_/g, " ")}
                    </span>
                    {rowTitle(it)}
                  </p>
                  <p className="text-white/40 text-xs">{rowSub(it)}</p>
                  {it.decision_note && (
                    <p className="text-white/50 text-[11px] italic mt-0.5">
                      note: {it.decision_note}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {it._pdf_url && (
                    <Button size="sm" variant="outline"
                            data-testid={`teacher-approved-pdf-${it._target_id}`}
                            onClick={() => printPdf(it)}>
                      Print PDF
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

